import { DATE_TEMPLATES, day, format, isSame } from '~/astra'
import { PlanningNoteAnalysisResponse } from '~/clients/derived-api-types'

import { SurgeryMetadataArgs } from '../../saveSurgeryMetadata'
import { getAvailabilityPeriods } from '../../SurgeryMetadata/availabilityPeriods'

export type AvailabilityPeriod = Exclude<NonNullable<PlanningNoteAnalysisResponse['availability_periods']>[number], { type: 'waiting' }>

export function formatSuggestedBool(value: boolean | null | undefined): string {
    if (value === true) return 'Ja'
    if (value === false) return 'Nei'
    return 'Ikke funnet'
}

export function formatSuggestedDate(value: string | null | undefined, dateFormat: string): string {
    if (!value) return 'Ikke funnet'
    const parsed = day(value)
    return parsed.isValid() ? format(parsed, dateFormat) : value
}

export function formatDateShort(value: string | null | undefined): string {
    if (!value) return ''
    const parsed = day(value)
    return parsed.isValid() ? format(parsed, DATE_TEMPLATES.dateShortMonth) : ''
}

export function isSameAvailabilityPeriod(a: AvailabilityPeriod | undefined, b: AvailabilityPeriod | undefined): boolean {
    if (!a && !b) return true
    if (!a || !b) return false
    if (a.type !== b.type) return false

    switch (a.type) {
        case 'available_from': {
            const bb = b as Extract<AvailabilityPeriod, { type: 'available_from' }>
            return isSame(day(a.start_date), day(bb.start_date), 'date')
        }
        case 'unavailable_between': {
            const bb = b as Extract<AvailabilityPeriod, { type: 'unavailable_between' }>
            return isSame(day(a.start_date), day(bb.start_date), 'date') && isSame(day(a.end_date), day(bb.end_date), 'date')
        }
    }
}

export function formatAvailabilityPeriod(period: AvailabilityPeriod, dateFormat: string): string {
    if (period.type === 'available_from') return `Tilgjengelig fra ${formatSuggestedDate(period.start_date, dateFormat)}`
    return `Utilgjengelig ${formatSuggestedDate(period.start_date, dateFormat)}–${formatSuggestedDate(period.end_date, dateFormat)}`
}

export function getAvailabilityLabel(period: AvailabilityPeriod): string {
    if (period.type === 'available_from') return `Tilgjengelig fra ${formatDateShort(period.start_date)}`
    return `Utilgjengelig ${formatDateShort(period.start_date)} - ${formatDateShort(period.end_date)}`
}

export function convertSurgeryMetadataArgsToPlanningNoteAnalysisResponse(args: SurgeryMetadataArgs): PlanningNoteAnalysisResponse {
    return {
        patient_ready: args.isCleared ?? null,
        patient_confirmed: args.isConfirmed,
        patient_prioritized: args.isPriority ?? null,
        patient_short_notice: args.isShortNotice ?? null,
        availability_periods: getAvailabilityPeriods(args.patientAvailability ?? []),
        is_waiting: args.isWaiting ?? null,
        surgery_cancer: args.isCancerSurgery ?? null,
        self_declaration_form: args.selfDeclarationFormNeedsFollowUp ?? null,
        external_document: args.externalDocumentNeedsFollowUp ?? null,
        test_results: args.testResultsNeedsFollowUp ?? null,
    }
}
